import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, MapPin, Users, Award, Zap, CheckCircle2,
  Building2, TrendingUp, Clock, Globe, Shield, Code, Phone, Mail
} from 'lucide-react';

const AIConsultingIndia = () => {
  const whyIndia = [
    {
      icon: Building2,
      title: 'India Headquarters',
      description: 'Ahmedabad-based with delivery teams across India Gujarat, Bangalore, Hyderabad, and Mumbai talent pools.',
      metric: 'Est. 2025',
    },
    {
      icon: Users,
      title: 'Senior Engineering Talent',
      description: '50+ senior AI and data engineers with global delivery experience, not fresher-heavy teams.',
      metric: '50+ engineers',
    },
    {
      icon: Clock,
      title: 'Speed to Production',
      description: 'Production-ready AI systems in 3-8 weeks. 60-80% faster than US or UK-based consulting firms.',
      metric: '3-8 weeks',
    },
    {
      icon: TrendingUp,
      title: 'Cost Advantage',
      description: 'Senior AI expertise at 40-60% lower cost than Silicon Valley or London rates, without sacrificing quality.',
      metric: '40-60% savings',
    },
    {
      icon: Globe,
      title: 'Global Client Base',
      description: 'From our India offices, we serve clients in the US, UK, Israel, Caribbean, and Southeast Asia.',
      metric: '200+ projects',
    },
    {
      icon: Award,
      title: 'Proven Track Record',
      description: '99% client satisfaction across 200+ AI and data engineering projects since 2025.',
      metric: '99% satisfaction',
    },
  ];

  const indiaIndustries = [
    {
      name: 'Pharmaceutical & Life Sciences',
      description: 'AI for drug discovery, clinical trial optimization, and patient monitoring for India\'s pharma sector.',
      examples: ['Drug discovery ML models', 'Clinical trial data analysis', 'Medical imaging AI', 'Regulatory compliance automation'],
    },
    {
      name: 'BFSI Banking, Finance, Insurance',
      description: 'Fraud detection, credit scoring, underwriting automation, and robo-advisory for Indian BFSI firms.',
      examples: ['Real-time fraud detection', 'AI credit scoring', 'Claims automation', 'Customer risk profiling'],
    },
    {
      name: 'Manufacturing & Industry 4.0',
      description: 'Predictive maintenance, quality inspection, and supply chain AI for Indian manufacturers.',
      examples: ['Predictive maintenance', 'Computer vision QC', 'Demand forecasting', 'OEE optimization AI'],
    },
    {
      name: 'Retail & E-commerce',
      description: 'Personalization engines, inventory optimization, and customer analytics for India\'s retail boom.',
      examples: ['Recommendation engines', 'Dynamic pricing AI', 'Inventory forecasting', 'Customer segmentation'],
    },
    {
      name: 'IT & SaaS Companies',
      description: 'AI feature development, RAG systems, and generative AI integrations for Indian SaaS and IT firms.',
      examples: ['LLM integration', 'AI-powered search', 'Automated support bots', 'Data pipeline modernization'],
    },
    {
      name: 'Healthcare & Hospitals',
      description: 'Clinical decision support, patient flow optimization, and administrative automation for Indian hospitals.',
      examples: ['Diagnostic AI', 'Patient scheduling AI', 'EHR data extraction', 'NLP for clinical notes'],
    },
  ];

  const deliveryModel = [
    {
      step: '01',
      title: 'Discovery Call',
      description: 'We understand your business problem, data maturity, and goals. 30-60 minute call, no commitment.',
    },
    {
      step: '02',
      title: 'Scoped Proposal',
      description: 'Detailed SOW with timeline, team structure, milestones, and fixed or T&M pricing. Delivered in 48 hours.',
    },
    {
      step: '03',
      title: 'Rapid Build',
      description: 'Senior engineers start immediately. Weekly demos, daily standups, and full code access throughout.',
    },
    {
      step: '04',
      title: 'Production Deployment',
      description: 'Live in 3-8 weeks. Handover documentation, training, and optional ongoing support packages.',
    },
  ];

  const metrics = [
    { metric: '200+', label: 'Projects Delivered', description: 'Across India, US, UK, Israel, Caribbean' },
    { metric: '99%', label: 'Client Satisfaction', description: 'Across all AI and data projects' },
    { metric: '3-8', label: 'Weeks to Deploy', description: 'Faster than any India-based competitor' },
    { metric: '50+', label: 'AI Engineers', description: 'Senior team, Ahmedabad HQ' },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What AI consulting services does Agility provide in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Agility provides AI consulting services across India including AI strategy, machine learning development, generative AI integration, data engineering, automation, and AI implementation. We work with companies in healthcare, BFSI, manufacturing, retail, and IT sectors."
        }
      },
      {
        "@type": "Question",
        "name": "Where is Agility based in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our headquarters is at 14th floor, Sun Gravitas, Shyamal Cross Road, Ahmedabad, Gujarat 380015. We serve clients across India including Mumbai, Bangalore, Delhi, Hyderabad, and Pune."
        }
      },
      {
        "@type": "Question",
        "name": "How fast can Agility deliver an AI project in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We deliver production-ready AI in 3-8 weeks depending on project scope. This is 60-80% faster than traditional consulting firms. We ship an automation project in 3-6 weeks, a data engineering pipeline in 4-8 weeks, and an ML model in 4-12 weeks."
        }
      },
    ]
  };

  return (
    <>
      <Helmet>
        <title>AI Consulting Company India | AI Consulting Services India | Agility</title>
        <meta
          name="description"
          content="Leading AI consulting company in India. Ahmedabad-headquartered, serving BFSI, pharma, manufacturing, retail, and IT clients across India. Production AI in 3-8 weeks. 200+ projects, 99% satisfaction. 40-60% lower cost than US firms."
        />
        <meta
          name="keywords"
          content="AI consulting company India, AI consulting services India, AI implementation India, machine learning consulting India, generative AI consulting India, AI consulting Ahmedabad, data engineering India, AI development company India"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-india" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting Company India | Agility" />
        <meta property="og:description" content="Leading AI consulting company in India. Production AI in 3-8 weeks, 40-60% cost advantage, 200+ projects delivered." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-india" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Agility - AI Consulting Company India",
            "description": "Leading AI consulting and machine learning services across India. Expert AI solutions for pharma, BFSI, manufacturing, retail, and IT sectors.",
            "url": "https://www.agilitytech.ai",
            "telephone": "+91-7041402034",
            "email": "hello@agilitytech.ai",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "14th floor, Sun Gravitas, Shyamal Cross Road, Rajmani Society",
              "addressLocality": "Ahmedabad",
              "addressRegion": "Gujarat",
              "postalCode": "380015",
              "addressCountry": "IN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "India"
            },
            "serviceType": "AI Consulting and Machine Learning Services"
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-900 via-black to-teal-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
              <MapPin className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">AI Consulting Company India</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              India's Fastest<br />
              <span className="text-teal-400">AI Consulting Company</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
              Production-ready AI in 3-8 weeks. Senior engineers, Ahmedabad HQ, global delivery.
              40-60% lower cost than US and UK AI consulting firms without the offshore quality compromise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
              >
                Get a Free AI Roadmap
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:+917041402034"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
              >
                <Phone className="w-5 h-5" />
                +91-7041402034
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {metrics.map((m, i) => (
                <div key={i} className="text-center">
                  <div className="text-4xl font-bold text-teal-400 mb-1">{m.metric}</div>
                  <div className="text-white font-semibold mb-1">{m.label}</div>
                  <div className="text-gray-400 text-sm">{m.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Agility for India */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Indian Companies Choose Agility
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Speed, quality, and execution without the overhead of large consulting firms.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyIndia.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <div className="text-2xl font-bold text-teal-600 mb-2">{item.metric}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              AI Consulting for India's Key Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-specific AI implementations with domain expertise not generic templates.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {indiaIndustries.map((ind, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{ind.name}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{ind.description}</p>
                <ul className="space-y-1">
                  {ind.examples.map((ex, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How We Work</h2>
            <p className="text-lg text-gray-600">From first call to production in a straight line no bureaucracy.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {deliveryModel.map((d, i) => (
              <div key={i} className="text-center">
                <div className="text-5xl font-bold text-teal-100 mb-3">{d.step}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{d.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location + contact */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our India Headquarters
              </h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Ahmedabad HQ</div>
                    <div className="text-gray-600">14th floor, Sun Gravitas, Shyamal Cross Road<br />Rajmani Society, Ahmedabad, Gujarat 380015</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <a href="tel:+917041402034" className="text-teal-600 hover:text-teal-700 font-medium mt-0.5">+91-7041402034</a>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <a href="mailto:hello@agilitytech.ai" className="text-teal-600 hover:text-teal-700 font-medium mt-0.5">hello@agilitytech.ai</a>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/ai-consulting-company-ahmedabad"
                  className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold"
                >
                  View Ahmedabad Office Details
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-6">We Serve Clients Across India</h3>
              {[
                ['Ahmedabad', 'Gujarat HQ pharma, textiles, finance, IT'],
                ['Mumbai', 'BFSI, media, e-commerce'],
                ['Bangalore', 'IT, SaaS, startups, healthtech'],
                ['Hyderabad', 'Pharma, IT, manufacturing'],
                ['Delhi / NCR', 'Government, BFSI, retail'],
                ['Pune', 'Manufacturing, automotive, IT'],
              ].map(([city, desc], i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <span className="font-semibold text-gray-900">{city}</span>
                  <span className="text-gray-500 text-sm">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqSchema.mainEntity.map((f: any, i: number) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.name}</h3>
                <p className="text-gray-600 leading-relaxed">{f.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Explore More</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'AI Consulting Ahmedabad', href: '/ai-consulting-company-ahmedabad' },
              { label: 'AI Consulting USA', href: '/ai-consulting-company-usa' },
              { label: 'Generative AI Consulting', href: '/generative-ai-consulting' },
              { label: 'AI Implementation Services', href: '/ai-implementation-services' },
              { label: 'Hire AI Developers', href: '/hire-ai-developers' },
              { label: 'AI for Healthcare', href: '/ai-consulting-healthcare' },
              { label: 'AI for Insurance', href: '/ai-consulting-insurance' },
              { label: 'Case Studies', href: '/case-studies' },
            ].map((link, i) => (
              <Link key={i} to={link.href} className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm group">
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-500 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build AI in India Fast?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Get a free AI readiness assessment and scoped proposal in 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
            >
              Schedule Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+917041402034"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all text-lg"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIConsultingIndia;
